/**
 * Baixa de uma conta — recebimento ou pagamento.
 *
 * O diálogo existe por causa de um detalhe que o banco já previu e a tela
 * precisa honrar: `valor_recebido_centavos` / `valor_pago_centavos` podem ser
 * DIFERENTES do previsto. Juro de atraso para mais, desconto de pontualidade
 * para menos — é o que acontece de fato no balcão. Uma baixa que só marca
 * "recebida" e assume o valor previsto obriga o lojista a corrigir por fora, e
 * a conciliação nunca fecha.
 *
 * Por isso o campo de valor vem PREENCHIDO com o previsto (o caso comum é um
 * Enter) mas editável, e a diferença é escrita em texto antes de confirmar —
 * ninguém deveria descobrir que recebeu R$ 12,00 a mais só no relatório.
 *
 * O front não faz aritmética decimal (CONSTITUICAO.md §1): o valor digitado
 * vira centavos por `dinheiro.analisar`, e a diferença é subtração de dois
 * inteiros.
 */
import { useState, type FormEvent } from 'react'

import { Campo } from '../../componentes/Campos'
import { CampoData } from '../../componentes/CampoData'
import { Icone } from '../../componentes/Icone'
import { ErroApi } from '../../plataforma/api'
import { analisar, formatar, formatarMoeda } from '../../plataforma/dinheiro'
import {
  baixarConta,
  diasDeAtraso,
  hojeISO,
  urgenciaDaConta,
  type Lado,
  type LinhaConta,
} from '../../plataforma/financeiro'
import { dataISOparaBR } from '../../plataforma/mascaras'
import { Dialogo, RodapeDoDialogo } from '../../componentes/Dialogo'

const TEXTO = {
  receber: {
    titulo: 'Receber',
    rotuloValor: 'Valor recebido',
    rotuloData: 'Data do recebimento',
    confirmar: 'Confirmar recebimento',
    confirmando: 'Recebendo…',
    pessoa: 'Cliente',
  },
  pagar: {
    titulo: 'Pagar',
    rotuloValor: 'Valor pago',
    rotuloData: 'Data do pagamento',
    confirmar: 'Confirmar pagamento',
    confirmando: 'Pagando…',
    pessoa: 'Fornecedor',
  },
} as const

export function DialogoDeBaixa({
  lado,
  linha,
  aoFechar,
  aoConcluir,
}: {
  lado: Lado
  linha: LinhaConta
  aoFechar: () => void
  /** Chamado depois da baixa gravada, com o aviso já escrito. */
  aoConcluir: (aviso: string) => void
}) {
  const texto = TEXTO[lado]

  // Já preenchido com o previsto: o caso comum é receber exatamente o que
  // estava combinado, e obrigar a redigitar o valor todo dia é fila no balcão.
  const [valor, setValor] = useState(() => formatar(linha.valor_centavos))
  const [data, setData] = useState(() => hojeISO())
  const [erroValor, setErroValor] = useState('')
  const [erroData, setErroData] = useState('')
  const [erro, setErro] = useState('')
  const [salvando, setSalvando] = useState(false)

  const centavos = analisar(valor)
  const diferenca = centavos === null ? 0 : centavos - linha.valor_centavos
  const atraso = diasDeAtraso(linha)
  const pessoa = lado === 'receber' ? linha.cliente : linha.fornecedor

  async function enviar(evento: FormEvent) {
    evento.preventDefault()
    setErro('')

    if (centavos === null) {
      setErroValor('Valor inválido. Escreva como 129,90.')
      return
    }
    if (centavos <= 0) {
      setErroValor('O valor da baixa precisa ser maior que zero.')
      return
    }
    setErroValor('')

    if (!data) {
      setErroData('Informe a data da baixa.')
      return
    }
    setErroData('')

    setSalvando(true)
    try {
      await baixarConta(lado, linha.codigo, { valor_centavos: centavos, data })
      aoConcluir(
        `${linha.codigo} ${lado === 'receber' ? 'recebida' : 'paga'} em ${dataISOparaBR(data)} — ${formatarMoeda(centavos)}.`,
      )
    } catch (causa: unknown) {
      /* 409 é "essa conta já foi baixada", e a API manda o texto pronto com a
         data. Trocar isso por "erro ao salvar" esconderia justamente o que a
         pessoa precisa saber. */
      setErro(
        causa instanceof ErroApi ? causa.message : 'Não foi possível registrar a baixa.',
      )
      setSalvando(false)
    }
  }

  return (
    <Dialogo
      icone={lado === 'receber' ? 'payments' : 'receipt_long'}
      titulo={`${texto.titulo} ${linha.codigo}`}
      descricao={
        <>
          {linha.descricao}
          {linha.total_parcelas > 1 && (
            <>
              {' · '}
              <span className="tabular">
                parcela {linha.numero_parcela}/{linha.total_parcelas}
              </span>
            </>
          )}
          {pessoa && ` · ${pessoa.nome}`}
        </>
      }
      aoFechar={aoFechar}
    >
      <form onSubmit={enviar} noValidate>
        {/* O previsto e o vencimento ficam à vista o tempo todo: é contra eles
            que a pessoa decide se o valor digitado está certo. */}
        <div className="mb-5 flex flex-wrap gap-x-8 gap-y-3 rounded-sistema border border-borda bg-fundo px-4 py-3">
          <div>
            <div className="text-[11px] font-bold tracking-[0.6px] text-texto-suave uppercase">
              Valor previsto
            </div>
            <div className="tabular text-[17px] font-bold text-tinta">
              {formatarMoeda(linha.valor_centavos)}
            </div>
          </div>
          <div>
            <div className="text-[11px] font-bold tracking-[0.6px] text-texto-suave uppercase">
              Vencimento
            </div>
            <div
              className={`tabular text-[17px] font-bold ${
                urgenciaDaConta(linha) === 'atrasada' ? 'text-erro' : 'text-tinta'
              }`}
            >
              {dataISOparaBR(linha.vencimento)}
            </div>
          </div>
        </div>

        {atraso > 0 && (
          <p className="mb-4 flex items-start gap-2 rounded-sistema bg-erro-suave px-3.5 py-2.5 text-[12.5px] text-erro-texto">
            <Icone nome="atraso" tamanho={16} className="mt-px shrink-0" />
            <span>
              Vencida há <span className="tabular">{atraso}</span>{' '}
              {atraso === 1 ? 'dia' : 'dias'}. Se houve juro ou multa, some ao valor abaixo.
            </span>
          </p>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Campo
            rotulo={texto.rotuloValor}
            value={valor}
            onChange={(evento) => setValor(evento.target.value)}
            erro={erroValor}
            inputMode="decimal"
            autoFocus
            placeholder="0,00"
          />
          <CampoData rotulo={texto.rotuloData} value={data} onChange={setData} erro={erroData} />
        </div>

        {/* Diferença é subtração de dois inteiros — o único cálculo de
            dinheiro que o front pode fazer (CONSTITUICAO.md §1). */}
        {diferenca !== 0 && !erroValor && (
          <p className="mt-3 flex items-start gap-2 rounded-sistema bg-atencao-suave px-3.5 py-2.5 text-[12.5px] text-atencao-texto">
            <Icone nome="info" tamanho={16} className="mt-px shrink-0" />
            <span>
              {formatarMoeda(Math.abs(diferenca))} {diferenca > 0 ? 'a mais' : 'a menos'} que o
              previsto
              {diferenca > 0
                ? ' — juro de atraso ou multa.'
                : ' — desconto de pontualidade ou acordo.'}
            </span>
          </p>
        )}

        {erro && (
          <p
            role="alert"
            className="mt-3 flex items-start gap-2 rounded-sistema bg-erro-suave px-3.5 py-2.5 text-[12.5px] font-medium text-erro-texto"
          >
            <Icone nome="error" tamanho={16} className="mt-px shrink-0" />
            <span>{erro}</span>
          </p>
        )}

        <RodapeDoDialogo
          confirmar={texto.confirmar}
          confirmando={texto.confirmando}
          ocupado={salvando}
          aoCancelar={aoFechar}
        />
      </form>
    </Dialogo>
  )
}
